/**
 * NEW_EXPERIENCE — the one switch the SIGNUP redesign ships behind
 * (docs/blocks/signup-epic.md § Ruled forks 1: plans merge to main DORMANT;
 * one constant flips it, its own commit, one revert).
 *
 * On for branch deploys and local dev; LOCKED OFF on main — the
 * exchangeVariant main-lock pattern. Deliberately NOT opened by the dev-tools
 * flag yet: `?dev=1` works on prod too, and new-experience code writes to
 * schema prod doesn't have until the flip chain (plan signup-foundations §
 * Prep findings 2). Open it to dev-tools when prod gets migration 031.
 *
 * Presentation + capture gate only: formation writes never read this.
 */

import { isBranchDeploy } from './devTools';

// The flip. Main serves the new experience only when this is true.
export const NEW_EXPERIENCE_ON_MAIN = false;

/**
 * Test seam `?nx=0` (signup E3a, F5): a branch-deploy page load carrying it
 * keeps the OLD design, so the e2e suites that guard today's guest path stay
 * green while branch deploys render the new screens. Read only on branch
 * deploys (inert on main, which is already old), never stored — this load
 * only. Snapshotted at module load, the exchangeVariant.captureSearch pattern:
 * App.js strips query params after its first route, so a live read would flip
 * the design mid-load. Off also turns off the new-experience writes for that
 * load — exactly main's behaviour.
 */
const captureOldDesignSeam = () => {
  try {
    return new URLSearchParams(window.location.search).get('nx') === '0';
  } catch {
    return false;
  }
};

const oldDesignThisLoad = captureOldDesignSeam();

export const isNewExperience = () =>
  NEW_EXPERIENCE_ON_MAIN || (isBranchDeploy() && !oldDesignThisLoad);

/**
 * D2 LIVE-SHARING READ PATH — its own switch, SEPARATE from NEW_EXPERIENCE and
 * OFF everywhere until the sharing screens AND the flip backfill exist
 * (f/d2-read-path-off, 2026-10-15).
 *
 * Why separate + off: the D2 read path is resolver-authoritative and WHOLESALE-
 * REPLACES each peer's cached shared details from get_shared_items. But
 * group_share_sets / connection_item_shares are EMPTY until the backfill, so the
 * resolver answers "shares nothing" for everyone — and the read path persists
 * that empty answer over received_contact, wiping every peer's stored phone/email
 * (observed on dev 2026-10-15) AND destroying the source the backfill copies from.
 * With this OFF, the new experience reads shared details the pre-D2 way (the
 * copy-push / hometown carry / history rebuild), exactly as before the D2 merge.
 *
 * The WRITE side + the tables stay merged and active under NEW_EXPERIENCE — only
 * the destructive READ is gated here.
 *
 * Turn on (set the constant true) ONLY after the backfill populates the tables,
 * per environment (new-experience-flip.md). It then follows the new experience.
 */
export const LIVE_SHARING_READ_ON = false;

export const isLiveSharingReadOn = () => LIVE_SHARING_READ_ON && isNewExperience();
